import axios from "axios";

const api = axios.create({
  baseURL: "/api",
  withCredentials: true,
});

// Auth services
export const login = (credentials) => api.post("/auth/login", credentials);
export const logout = () => api.get("/auth/logout");
export const getMe = () => api.get("/auth/me");
export const registerUser = async (userData) => {
  const res = await api.post("/auth/register", userData);
  return res.data;
};
export const adminCreateUser = async (userData) => {
  const res = await api.post("/auth/users", userData);
  return res.data;
};
export const updateUser = async (id, userData) => {
  const res = await api.put(`/auth/users/${id}`, userData);
  return res.data;
};
export const getAllUsers = async () => {
  const res = await api.get("/auth/users");
  return res.data;
};
export const deleteUser = async (id) => {
  const res = await api.delete(`/auth/users/${id}`);
  return res.data;
};

export const getConfirmationAgents = async () => {
  const res = await api.get("/auth/users/confirmation-agents");
  return res.data;
};

export const createStore = async (data) => {
  const res = await api.post("/onboarding/create-store", data);
  return res.data;
};

// Orders services
export const getOrders = async (params) => {
  const res = await api.get("/orders", { params });
  return res.data;
};

export const syncOrders = async () => {
  const res = await api.post("/orders/sync");
  return res.data;
};

export const confirmOrder = async (id, confirmationData) => {
  const res = await api.patch(`/orders/${id}/confirm`, confirmationData);
  return res.data;
};

export const assignOrder = async (id, assignedToUserId) => {
  const res = await api.put(`/orders/${id}/assign`, { assignedToUserId });
  return res.data;
};

export const deleteOrder = async (id) => {
  const res = await api.delete(`/orders/${id}`);
  return res.data;
};

export const createManualOrder = async (orderData) => {
  const res = await api.post("/orders/manual-order", orderData);
  return res.data;
};

export const getShippingQueue = async () => {
  const res = await api.get("/orders/shipping-queue");
  return res.data;
};

export const sendToDelivery = async (id, providerSlug) => {
  const res = await api.post(`/orders/${id}/send-to-delivery`, { providerSlug });
  return res.data;
};

export const bulkSendToDelivery = async (orderIds, providerSlug) => {
  const res = await api.post(`/orders/bulk-send-to-delivery`, { orderIds, providerSlug });
  return res.data;
};

// Delivery Companies services
export const getDeliveryCompanies = async () => {
  const res = await api.get("/delivery-companies");
  return res.data;
};

// Products services
export const getProducts = async (params) => {
  const res = await api.get("/products", { params });
  return res.data;
};
export const createProduct = async (userData) => {
  const res = await api.post("/products", userData);
  return res.data;
};
export const updateProduct = async (id, userData) => {
  const res = await api.put(`/products/${id}`, userData);
  return res.data;
};
export const deleteProduct = async (id) => {
  const res = await api.delete(`/products/${id}`);
  return res.data;
};

// Variant services
export const getProductVariants = async (productId) => {
  const res = await api.get(`/products/${productId}/variants`);
  return res.data;
};
export const upsertVariant = async (productId, variantData) => {
  const res = await api.post(`/products/${productId}/variants`, variantData);
  return res.data;
};
export const updateVariantStock = async (productId, variantId, quantity) => {
  const res = await api.patch(`/products/${productId}/variants/${variantId}/stock`, { quantity });
  return res.data;
};
export const deleteVariant = async (productId, variantId) => {
  const res = await api.delete(`/products/${productId}/variants/${variantId}`);
  return res.data;
};

// Stock services (Returned orders)
export const getReturnedOrdersCount = async () => {
  const res = await api.get("/stock/returned-count");
  return res.data;
};

export const getReturnedOrders = async () => {
  const res = await api.get("/stock/returned-orders");
  return res.data;
};

export const restockOrder = async (orderId) => {
  const res = await api.post(`/stock/restock/${orderId}`);
  return res.data;
};


export const connectDeliveryCompany = async (id, credentials, settings = {}) => {
  const res = await api.patch(`/delivery-companies/${id}/connect`, { credentials, settings });
  return res.data;
};

export const disconnectDeliveryCompany = async (id) => {
  const res = await api.patch(`/delivery-companies/${id}/disconnect`);
  return res.data;
};

export const makeDefaultDeliveryCompany = async (id) => {
  const res = await api.patch(`/delivery-companies/${id}/make-default`);
  return res.data;
};

export const testDeliveryConnection = async (id) => {
  const res = await api.post(`/delivery-companies/${id}/test`);
  return res.data;
};

// Dashboard services
export const getDashboardStats = async (from, to) => {
  let url = `/dashboard?from=${from || ''}&to=${to || ''}`;
  const response = await api.get(url);
  return response.data;
};

// Tracking Sync services
export const syncTrackingStatuses = async (providerSlug) => {
  const res = await api.post("/sync/tracking", { providerSlug });
  return res.data; // { success, jobId, status }
};

export const getSyncJobStatus = async (jobId) => {
  const res = await api.get(`/sync/tracking/status/${jobId}`);
  return res.data;
};

export const getDeliveryTracking = async (params) => {
  const res = await api.get("/orders/delivery-tracking", { params });
  return res.data;
};

export const syncOrderTracking = async (id) => {
  const res = await api.post(`/orders/${id}/sync-tracking`);
  return res.data;
};

export const getPayroll = async (from, to) => {
  const res = await api.get("/payroll", { params: { from, to } });
  return res.data;
};

export const pingPresence = async () => {
  const res = await api.post("/presence/ping");
  return res.data;
};

// Settings services
export const getSettings = async () => {
  const res = await api.get("/settings");
  return res.data;
};

export const updateBrandSettings = async (data) => {
  const res = await api.put("/settings/brand", data);
  return res.data;
};

export const updateProfile = async (data) => {
  const res = await api.put("/settings/profile", data);
  return res.data;
};

export const changePassword = async (data) => {
  const res = await api.put("/settings/password", data);
  return res.data;
};

// Chat services
export const getChatMessages = async () => {
  const res = await api.get("/chat/messages");
  return res.data;
};

export const sendChatMessage = async (message) => {
  const res = await api.post("/chat/messages", { message });
  return res.data;
};

export const getChatUnreadCount = async () => {
  const res = await api.get("/chat/unread-count");
  return res.data;
};

export const markChatSeen = async () => {
  const res = await api.post("/chat/mark-seen");
  return res.data;
};

export default api;
